import { Alert, Platform } from 'react-native';

import {
  hasAppDialogHost,
  requestAppChoice,
  requestAppDialog,
  type AppDialogOption,
} from './appDialogBridge';

/**
 * Подтверждение действия, работающее и в вебе (стори 18.2).
 *
 * <p>`Alert.alert` из react-native-web — **пустая функция** (`static alert() {}` в
 * `react-native-web/dist/exports/Alert`). То есть подтверждение через него в браузере не
 * показывается, `onPress` не вызывается никогда, и действие тихо не выполняется: кнопка нажата,
 * а не произошло ничего. Для «вы уверены?» это худший из возможных исходов.
 *
 * <p>Поэтому в вебе вопрос задаёт собственный диалог в цветах активной темы
 * ({@link ../components/AppDialogHost}), а на устройствах — системный `Alert`. Почему на
 * телефоне намеренно оставлен системный, написано там же.
 *
 * <p>Раньше в вебе тут стоял `window.confirm`: он работал, но показывал белую системную
 * карточку посреди тёмного приложения, со своими кнопками на языке браузера. Он остаётся
 * запасным путём — на случай, если хост почему-то не смонтирован (ранний старт, тест).
 *
 * <p>Если диалог показать нечем совсем (нет и `confirm`), считаем ответ утвердительным:
 * подтверждение — дополнительная страховка поверх осознанного нажатия кнопки, и ломать из-за
 * неё саму операцию нельзя.
 */
export function confirmAction(options: {
  title: string;
  body: string;
  confirmText: string;
  cancelText: string;
  destructive?: boolean;
}): Promise<boolean> {
  const { title, body, confirmText, cancelText, destructive } = options;

  if (Platform.OS === 'web') {
    if (hasAppDialogHost()) {
      return requestAppDialog({ kind: 'confirm', title, body, confirmText, cancelText, destructive });
    }
    const ask = (globalThis as { confirm?: (message?: string) => boolean }).confirm;
    if (typeof ask !== 'function') {
      return Promise.resolve(true);
    }
    return Promise.resolve(ask(`${title}\n\n${body}`));
  }

  return new Promise<boolean>((resolve) => {
    Alert.alert(
      title,
      body,
      [
        { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
        {
          text: confirmText,
          style: destructive ? 'destructive' : 'default',
          onPress: () => resolve(true),
        },
      ],
      // Свайп/«назад» поверх диалога — это отказ, а не согласие.
      { cancelable: true, onDismiss: () => resolve(false) },
    );
  });
}

/**
 * Сообщение без вопроса: «готово», «не удалось», «так нельзя».
 *
 * <p><b>Зачем отдельная функция, а не `Alert.alert`.</b> В вебе `Alert.alert` не делает
 * ничего. То есть «Избранное очищено» и «Часть сообщений удалить не удалось» там не
 * показывались вовсе — человек нажимал «Очистить» и не получал ни подтверждения, ни ошибки, и
 * отличить «сработало» от «отвалилось» мог только по опустевшему экрану. Здесь та же развилка,
 * что у {@link confirmAction}: в вебе своя карточка в цветах темы, на телефоне системный
 * `Alert`.
 *
 * <p>`okText` — подпись единственной кнопки; она приходит снаружи, потому что этот модуль про
 * платформу, а не про язык, и своего доступа к переводам у него нет.
 *
 * <p>Промис разрешается, когда сообщение закрыли. Ждать его никто не обязан: у уведомления
 * один исход, и `void notifyAction(...)` — нормальный способ вызова.
 */
export function notifyAction(options: {
  title: string;
  body?: string;
  okText: string;
}): Promise<boolean> {
  const { title, body = '', okText } = options;

  if (Platform.OS === 'web') {
    if (hasAppDialogHost()) {
      return requestAppDialog({ kind: 'notice', title, body, confirmText: okText });
    }
    const tell = (globalThis as { alert?: (message?: string) => void }).alert;
    if (typeof tell === 'function') {
      tell(body ? `${title}\n\n${body}` : title);
    }
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    Alert.alert(title, body || undefined, [{ text: okText, onPress: () => resolve(true) }], {
      cancelable: true,
      onDismiss: () => resolve(true),
    });
  });
}

/**
 * Меню из нескольких вариантов («из галереи / снять / отмена»): то, что `confirmAction` с его
 * двумя ответами не умеет.
 *
 * <p>Промис отдаёт индекс нажатой кнопки — любой, в том числе с `style: 'cancel'`, — или
 * `null`, если меню закрыли мимо кнопок («назад», тап по фону). Развилка платформ та же: в
 * вебе своя карточка, на телефоне системный `Alert`.
 *
 * <p>Если в вебе хоста нет, выбирать не из чего: `window.confirm` отвечает только да/нет. Тогда
 * ответ `null` — «ничего не выбрано», а не зависший навсегда промис.
 */
export function chooseAction(options: {
  title: string;
  body?: string;
  buttons: AppDialogOption[];
}): Promise<number | null> {
  const { title, body = '', buttons } = options;

  if (Platform.OS === 'web') {
    if (hasAppDialogHost()) {
      return requestAppChoice({ title, body, options: buttons });
    }
    return Promise.resolve(null);
  }

  return new Promise<number | null>((resolve) => {
    Alert.alert(
      title,
      body || undefined,
      buttons.map((b, index) => ({ text: b.text, style: b.style, onPress: () => resolve(index) })),
      { cancelable: true, onDismiss: () => resolve(null) },
    );
  });
}
